import React, { useEffect, useRef, createContext, useContext, useState } from 'react';

// Create a context to manage the TradingView library loading state
const TradingViewContext = createContext({
  isLoaded: false,
  setIsLoaded: () => {},
});

// Provider component to load the TradingView library once
export const TradingViewProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      console.log('TradingViewProvider: Initializing...');
      hasInitializedRef.current = true;

      if (!isLoaded && !window.TradingView) {
        console.log('TradingViewProvider: Loading TradingView script...');
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => {
          console.log('TradingViewProvider: Script loaded successfully');
          setIsLoaded(true);
        };
        script.onerror = (error) => {
          console.error('TradingViewProvider: Error loading script:', error);
        };
        document.head.appendChild(script);

        return () => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      }
    }
  }, [isLoaded]);

  return (
    <TradingViewContext.Provider value={{ isLoaded, setIsLoaded }}>
      {children}
    </TradingViewContext.Provider>
  );
};

// Individual chart component
const TradingViewChart = ({ 
  symbol = 'BTC.D', 
  theme = 'light', 
  height = '500px', 
  width = '100%',
  hideBorder = true,
  interval = 'D',
  hideTopToolbar = false,
  hideSideToolbar = true,
  hideLegend = false,
  allowSymbolChange = true,
  ...additionalOptions
}) => {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const { isLoaded } = useContext(TradingViewContext);
  const containerId = useRef(`tradingview_${symbol.replace(/[^a-zA-Z0-9]/g, '_')}`);
  
  // Initialize or reinitialize widget
  useEffect(() => {
    const currentContainer = containerRef.current;
    
    if (!hasInitializedRef.current && isLoaded && currentContainer && !widgetRef.current) {
      console.log('TradingViewChart: Initializing widget...');
      hasInitializedRef.current = true;

      try {
        if (!window.TradingView?.widget) {
          console.error('TradingViewChart: Widget not available');
          return;
        }

        // Ensure container is empty before creating new widget
        if (currentContainer) {
          currentContainer.innerHTML = '';
        }

        // Parse height to ensure it's a number
        const parsedHeight = parseInt(height, 10);
        if (isNaN(parsedHeight)) {
          console.error('TradingViewChart: Invalid height value:', height);
          return;
        }

        // Basic widget options with strict typing
        const widgetOptions = {
          autosize: false,
          width: '100%',
          height: parsedHeight,
          symbol: String(symbol),
          interval: String(interval),
          timezone: 'Etc/UTC',
          theme: String(theme),
          style: '1',
          locale: 'en',
          enable_publishing: false,
          hide_top_toolbar: Boolean(hideTopToolbar),
          hide_side_toolbar: Boolean(hideSideToolbar),
          hide_legend: Boolean(hideLegend),
          allow_symbol_change: Boolean(allowSymbolChange),
          container_id: String(containerId.current),
          hide_volume: false,
          withdateranges: false,
          save_image: false,
          details: false,
          hotlist: false,
          calendar: false,
          show_popup_button: false,
          hide_cross_cursor: false,
          hideideas: true,
        };
        
        console.log('TradingViewChart: Creating widget with options:', widgetOptions);
        widgetRef.current = new window.TradingView.widget(widgetOptions);
      } catch (error) {
        console.error('TradingViewChart: Error creating widget:', error);
      }
    }
    
    // Cleanup when component unmounts
    return () => {
      if (currentContainer) {
        currentContainer.innerHTML = '';
      }
      widgetRef.current = null;
      hasInitializedRef.current = false;
    };
  }, [isLoaded, symbol, theme, height, interval, hideTopToolbar, hideSideToolbar, hideLegend, allowSymbolChange]);

  const borderStyle = hideBorder ? { border: 'none' } : { border: '1px solid #e1e4e8' };
  
  return (
    <div className="tradingview-chart-container" style={{ width, ...borderStyle }}>
      <div 
        id={containerId.current} 
        ref={containerRef} 
        style={{ height, width: '100%' }}
      />
    </div>
  );
};

export default TradingViewChart;